import React from 'react'
import { SurfacePalette, type } from '../tokens'
import { SECTIONS, SectionKey, sectionNumberFor } from './sections'

interface Props {
  palette: SurfacePalette
  activeKey: SectionKey
  onOpenAllComments: () => void
  /** Sum of unresolved root-comment counts across the packet — drives the badge. */
  unresolvedTotal: number
}

const handleClick = (key: SectionKey) => () => {
  const el = document.getElementById(key)
  if (!el) return
  window.history.replaceState(null, '', '#' + key)
  el.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

export const EditorTOC = ({ palette, activeKey, onOpenAllComments, unresolvedTotal }: Props) => (
  <aside
    className="sticky top-0 self-start w-64 shrink-0"
    style={{
      background: palette.surface,
      borderRight: `1px solid ${palette.hairline}`,
      padding: '20px 12px',
      fontFamily: type.family.sans,
    }}
  >
    <button
      type="button"
      onClick={onOpenAllComments}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        width: '100%',
        textAlign: 'left',
        padding: '8px 12px',
        marginBottom: 16,
        borderRadius: 6,
        border: `1px solid ${palette.hairline}`,
        background: 'transparent',
        color: palette.text,
        fontFamily: type.family.sans,
        fontSize: type.size.body,
        fontWeight: type.weight.medium,
        cursor: 'pointer',
      }}
    >
      <span>All comments</span>
      {unresolvedTotal > 0 && (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 18,
            height: 18,
            padding: '0 5px',
            borderRadius: 999,
            background: palette.accent,
            color: palette.accentInk,
            fontFamily: type.family.mono,
            fontSize: 11,
            fontWeight: type.weight.semibold,
            lineHeight: 1,
          }}
        >
          {unresolvedTotal}
        </span>
      )}
    </button>
    <div
      style={{
        fontFamily: type.family.mono,
        fontSize: type.size.eyebrow,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: palette.textMuted,
        padding: '0 12px',
        marginBottom: 12,
      }}
    >
      Sections
    </div>
    <nav aria-label="Sprint packet sections" style={{ display: 'flex', flexDirection: 'column' }}>
      {SECTIONS.map(s => {
        const active = s.key === activeKey
        return (
          <button
            key={s.key}
            type="button"
            onClick={handleClick(s.key)}
            aria-current={active ? 'location' : undefined}
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 10,
              width: '100%',
              textAlign: 'left',
              padding: '8px 12px',
              border: 'none',
              borderLeft: `2px solid ${active ? palette.accent : 'transparent'}`,
              background: 'transparent',
              color: active ? palette.accent : palette.text,
              fontSize: type.size.body,
              fontWeight: active ? type.weight.semibold : type.weight.regular,
              cursor: 'pointer',
              transition: 'color 120ms ease, border-color 120ms ease',
            }}
          >
            <span
              style={{
                fontFamily: type.family.mono,
                fontSize: type.size.eyebrow,
                color: active ? palette.accent : palette.textFaint,
                letterSpacing: '0.08em',
              }}
            >
              {sectionNumberFor(s.key)}
            </span>
            <span>{s.label}</span>
          </button>
        )
      })}
    </nav>
  </aside>
)
